/**
 * Provenance primitives.
 *
 * ONE RULE, from the design brief: provenance sits ADJACENT TO THE VALUE it
 * qualifies — never in a tooltip, never on a settings page, never below the
 * fold. These figures travel as screenshots and anything outside the crop is
 * lost.
 *
 * COLOUR IS SEMANTIC AND THERE IS NO RED. Absence and refusal are not
 * failures: `refuse` means the parser declined to make a claim, which is the
 * system working. Actual failure has its own primitive, `Failed`, and its own
 * treatment. A refusal painted red gets worked around rather than fixed.
 */

import { useId, useState, type ReactNode } from 'react';
import './provenance.css';

type Tone = 'assumed' | 'baseline' | 'confirmed' | 'caution' | 'muted' | 'identity';

const TONE: Record<Tone, string> = {
  identity: 'pem-badge--live pem-badge--pill',   // cyan: active / current
  confirmed: 'pem-badge--confirmed',              // green: a human decided
  caution: 'pem-badge--caution',                  // amber: needs interpretation
  baseline: 'pem-badge--outline',
  assumed: 'pem-badge--assumed',
  muted: 'pem-badge--quiet',                      // grey: absent / unavailable
};

export function Tag({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`pem-badge ${TONE[tone]}`}>{children}</span>;
}

/** A short line beneath a value. 9px, uppercase, muted — the register the
 *  design reserves for "where this came from". */
export function Note({ children }: { children: ReactNode }) {
  return <p className="pem-provenance pem-note">{children}</p>;
}

/**
 * Progressive disclosure for evidence.
 *
 * The summary carries the concise provenance a reader needs at a glance; the
 * body carries locators, relationships and confirmation identity. Coordinates
 * and raw structure belong HERE and never in the default view — the
 * structured evidence exists for traceability, and rendering it for humans is
 * a different job from storing it.
 */
export function Evidence({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="pem-evidence">
      <button
        type="button"
        className="pem-evidence__toggle"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? '−' : '+'} {label}
      </button>
      {open && <dl className="pem-evidence__body" id={id}>{children}</dl>}
    </div>
  );
}

export function Fact({ term, children }: { term: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** A figure with its unit, tabular so a digit changing does not reflow. */
export function Figure({ value, unit }: { value: string | number; unit?: string }) {
  return (
    <span className="pem-figure">
      {value}
      {unit && <span className="pem-unit">{unit}</span>}
    </span>
  );
}
